import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';
import type { Json } from '@/integrations/supabase/types';

export interface UnifiedNotificationRequest {
  notificationType: string;
  recipientUserId?: string;
  recipientEmail?: string;
  templateVariables?: Record<string, any>;
  urgency?: 'low' | 'medium' | 'high' | 'critical';
  context?: Record<string, any>;
}

export interface UnifiedNotificationResult {
  success: boolean;
  notificationId?: string;
  deliveryChannels?: Record<string, any>;
  urgency?: string;
  error?: string;
}

/**
 * Unified Notification Orchestrator Service
 * 
 * This service provides a single interface for all notification needs.
 * It automatically handles:
 * - Toast notifications (immediate)
 * - In-app notifications (persistent) 
 * - Email notifications (based on rules)
 * - Future channels (SMS, push, etc.)
 * 
 * Usage:
 * ```typescript
 * import { notify } from '@/services/unifiedNotificationOrchestratorService';
 * 
 * // Simple notification
 * await notify({
 *   notificationType: 'application_received',
 *   recipientUserId: userId,
 *   templateVariables: { job_title: 'Software Engineer' }
 * });
 * 
 * // Critical notification (bypasses user preferences)
 * await notify({
 *   notificationType: 'system_maintenance',
 *   recipientUserId: userId,
 *   urgency: 'critical',
 *   templateVariables: { message: 'System will be down in 5 minutes' }
 * });
 * ```
 */
// Utilities to keep stage names friendly in UI and templates
function mapStageTypeToName(stageType?: string): string {
  switch ((stageType || '').toLowerCase()) {
    case 'ai_interview':
      return 'AI Interview';
    case 'quiz':
      return 'Quiz Assessment';
    case 'assignment':
      return 'Assignment';
    case 'resume_screening':
      return 'Resume Screening';
    case 'profile_completion':
      return 'Profile Completion';
    case 'phone_screen':
      return 'Phone Screen';
    case 'technical_interview':
      return 'Technical Interview';
    case 'final_interview':
      return 'Final Interview';
    case 'offer':
      return 'Offer';
    case 'application_review':
      return 'Application Review';
    default:
      return 'Application Stage';
  }
}

function isUuidLike(value?: string): boolean {
  return !!value && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

function sanitizeStageAssignedVariables(vars: Record<string, any> = {}): Record<string, any> {
  const stageName = vars.stage_name || vars.stage_title || vars.stage;
  if (isUuidLike(stageName)) {
    const friendly = mapStageTypeToName(vars.stage_type);
    return { ...vars, stage_name: friendly, stage_title: friendly, stage: friendly };
    }
  if (!stageName && vars.stage_type) {
    const friendly = mapStageTypeToName(vars.stage_type);
    return { ...vars, stage_name: friendly, stage_title: friendly, stage: friendly };
  }
  return vars;
}

class UnifiedNotificationOrchestratorService {
  /**
   * Send a unified notification that will be delivered through appropriate channels
   * based on delivery rules and user preferences
   */
  async notify(request: UnifiedNotificationRequest): Promise<UnifiedNotificationResult> {
    try {
      // Validate request
      if (!request.notificationType) {
        throw new Error('notificationType is required');
      }

      if (!request.recipientUserId && !request.recipientEmail) {
        throw new Error('Either recipientUserId or recipientEmail is required');
      }

      // Prepare variables with stage name sanitization for stage_assigned
      const sanitizedVars = request.notificationType === 'stage_assigned'
        ? sanitizeStageAssignedVariables(request.templateVariables || {})
        : (request.templateVariables || {});

      // Call the unified notification database function
      const { data, error } = await supabase.rpc('unified_notify_enhanced', {
        notification_type_param: request.notificationType,
        recipient_user_id_param: request.recipientUserId || null,
        urgency_level_param: request.urgency || 'medium',
        context_param: {
          ...sanitizedVars,
          ...(request.context || {}),
          recipient_email: request.recipientEmail || null
        }
      });

      if (error) {
        console.error('Error calling unified_notify:', error);
        // For backend errors, don't show user-facing notifications
        // Just log silently and return failure
        return {
          success: false,
          error: 'Notification service temporarily unavailable'
        };
      }

      const result = (data as unknown) as UnifiedNotificationResult;
      
      if (!result.success) {
        console.error('Unified notification failed:', result.error);
        return result;
      }

      // Process immediate channels (toast, in-app are handled by database)
      await this.processImmediateChannels(request, result);

      // Process delayed channels (email, SMS, etc.)
      await this.processDelayedChannels(request, result);

      return result;

    } catch (error) {
      console.error('Unified notification orchestrator error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Process channels that should be delivered immediately (toast notifications)
   */
  private async processImmediateChannels(
    request: UnifiedNotificationRequest, 
    result: UnifiedNotificationResult
  ): Promise<void> {
    const channels = result.deliveryChannels || {};
    
    // Show toast notification if enabled and user is currently active
    if (channels.toast?.queued && request.recipientUserId) {
      try {
        // Only show toast for the current user
        const { data: { user } } = await supabase.auth.getUser();
        if (user?.id === request.recipientUserId) {
          this.showToastNotification(request);
        }
      } catch (error) {
        console.warn('Failed to show toast notification:', error);
      }
    }
  }

  /**
   * Process channels that can be delayed (email, SMS, push)
   */
  private async processDelayedChannels(
    request: UnifiedNotificationRequest,
    result: UnifiedNotificationResult
  ): Promise<void> {
    const channels = result.deliveryChannels || {};

    // Queue email notification if enabled
    if (channels.email?.queued) {
      try {
        await this.queueEmailNotification(request, channels.email.delay_minutes || 0);
      } catch (error) {
        console.warn('Failed to queue email notification:', error);
        // Update delivery log with failure
        this.logDeliveryFailure(result.notificationId, 'email', error);
      }
    }

    // Future: Queue SMS, push notifications, etc.
  }

  /**
   * Show toast notification using Sonner
   */
  private showToastNotification(request: UnifiedNotificationRequest): void {
    const { templateVariables = {}, urgency = 'medium' } = request;
    
    const title = templateVariables.title || this.getDefaultTitle(request.notificationType);
    const message = templateVariables.message || this.getDefaultMessage(request.notificationType);
    
    try {
      switch (urgency) {
        case 'critical':
          toast.error(title, { description: message, duration: 10000 });
          break;
        case 'high':
          toast.warning(title, { description: message, duration: 6000 });
          break;
        case 'low':
          toast.info(title, { description: message, duration: 3000 });
          break;
        default:
          toast.success(title, { description: message, duration: 4000 });
      }
    } catch (error) {
      console.warn('Failed to show toast notification:', error);
    }
  }

  /**
   * Queue email notification for delivery - UNIFIED QUEUE APPROACH
   */
  private async queueEmailNotification(
    request: UnifiedNotificationRequest,
    delayMinutes: number = 0
  ): Promise<void> {
    try {
      const scheduledAt = new Date(Date.now() + delayMinutes * 60 * 1000);
      
      // Use the queue_notification database function for unified queuing
      const { data, error } = await supabase.rpc('queue_notification', {
        notification_type_param: request.notificationType,
        template_id_param: request.notificationType,
        recipient_user_id_param: request.recipientUserId || null,
        recipient_email_param: request.recipientEmail || null,
        variables_param: request.templateVariables || {},
        priority_param: request.urgency === 'critical' ? 1 : request.urgency === 'high' ? 2 : request.urgency === 'medium' ? 3 : 4,
        scheduled_at_param: scheduledAt.toISOString()
      });

      if (error) {
        console.error('Error queuing email notification:', error);
        throw error;
      }

      console.log(`Email notification queued successfully: ${request.notificationType} for ${request.recipientUserId || request.recipientEmail}`);
    } catch (error) {
      console.error('Failed to queue email notification:', error);
      throw error;
    }
  }

  /**
   * Send email notification immediately - ONLY FOR EMERGENCY FALLBACK
   * Regular notifications should use the queue for consistency
   */
  private async sendEmailNotificationNow(request: UnifiedNotificationRequest): Promise<void> {
    try {
      console.warn('Using emergency fallback direct email sending for:', request.notificationType);
      
      // Get notification template info to build universal template variables
      const { data: notificationTemplate } = await supabase
        .from('notification_templates')
        .select('id, title_template, variables')
        .eq('id', request.notificationType)
        .eq('is_active', true)
        .single();

      if (!notificationTemplate) {
        console.warn(`No active notification template found for type: ${request.notificationType}`);
        return;
      }

      // Transform variables for the universal template
      const universalVariables = this.transformToUniversalTemplate(
        request.notificationType,
        notificationTemplate,
        request.templateVariables || {}
      );

      // Use the universal business notification template via direct call
      const { error } = await supabase.functions.invoke('send-notification-email', {
        body: {
          templateId: 'universal_business_notification',
          userId: request.recipientUserId,
          email: request.recipientEmail,
          variables: universalVariables
        }
      });

      if (error) {
        throw error;
      }

      console.log('Emergency direct email sent successfully');
      
      // Update delivery log with success
      if (request.recipientUserId) {
        await this.updateDeliveryStatus(request.recipientUserId, 'email', 'sent');
      }
    } catch (error) {
      console.error('Failed to send emergency direct email notification:', error);
      // Don't bubble up email errors to avoid showing backend issues to users
      // Just log them silently
    }
  }

  /**
   * Update delivery status in the log
   */
  private async updateDeliveryStatus(
    userId: string,
    channel: string,
    status: 'sent' | 'delivered' | 'failed'
  ): Promise<void> {
    try {
      await supabase
        .from('notification_delivery_log')
        .update({
          status,
          sent_at: status === 'sent' ? new Date().toISOString() : undefined,
          delivered_at: status === 'delivered' ? new Date().toISOString() : undefined
        })
        .eq('user_id', userId)
        .eq('channel', channel)
        .eq('status', 'queued')
        .order('created_at', { ascending: false })
        .limit(1);
    } catch (error) {
      console.warn('Failed to update delivery status:', error);
    }
  }

  /**
   * Log delivery failure
   */
  private async logDeliveryFailure(
    notificationId: string | undefined,
    channel: string,
    error: any
  ): Promise<void> {
    if (!notificationId) return;

    try {
      await supabase
        .from('notification_delivery_log')
        .update({
          status: 'failed',
          error_message: error instanceof Error ? error.message : String(error)
        })
        .eq('notification_id', notificationId)
        .eq('channel', channel);
    } catch (logError) {
      console.warn('Failed to log delivery failure:', logError);
    }
  }

  /**
   * Get default title for notification type
   */
  private getDefaultTitle(notificationType: string): string {
    const titleMap: Record<string, string> = {
      'application_received': 'New Application',
      'application_submitted': 'Application Submitted',
      'company_invitation': 'Company Invitation',
      'interview_scheduled': 'Interview Scheduled',
      'system_maintenance': 'System Maintenance',
      'team_member_added': 'New Team Member'
    };
    
    return titleMap[notificationType] || 'Notification';
  }

  /**
   * Get default message for notification type
   */
  private getDefaultMessage(notificationType: string): string {
    const messageMap: Record<string, string> = {
      'application_received': 'You have received a new job application',
      'application_submitted': 'Your application has been submitted successfully',
      'company_invitation': 'You have been invited to join a company',
      'interview_scheduled': 'Your interview has been scheduled',
      'system_maintenance': 'System maintenance notification',
      'team_member_added': 'A new team member has been added'
    };
    
    return messageMap[notificationType] || 'You have a new notification';
  }

  /**
   * Get user notification preferences
   */
  async getUserPreferences(userId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('user_notification_preferences')
      .select('*')
      .eq('user_id', userId);

    if (error) {
      console.error('Error fetching user preferences:', error);
      return [];
    }

    return data || [];
  }

  /**
   * Update user notification preferences
   */
  async updateUserPreference(
    userId: string,
    notificationType: string,
    channel: string,
    isEnabled: boolean,
    frequency?: string
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('user_notification_preferences')
        .upsert({
          user_id: userId,
          notification_type: notificationType,
          channel,
          is_enabled: isEnabled,
          frequency: frequency || 'immediate'
        });

      if (error) {
        console.error('Error updating user preference:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error updating user preference:', error);
      return false;
    }
  }

  /**
   * Get delivery analytics for a user
   */
  async getDeliveryAnalytics(userId: string, days: number = 30): Promise<any[]> {
    const { data, error } = await supabase
      .from('notification_delivery_log')
      .select('*')
      .eq('user_id', userId)
      .gte('created_at', new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString())
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching delivery analytics:', error);
      return [];
    }

    return data || [];
  }

  /**
   * Transforms notification-specific variables into universal template variables
   */
  private transformToUniversalTemplate(
    notificationType: string,
    notificationTemplate: any,
    originalVariables: Record<string, any>
  ): Record<string, any> {
    const baseVariables = {
      notification_title: notificationTemplate.title_template || this.getDefaultTitle(notificationType),
      notification_category: this.getCategoryFromType(notificationType),
      recipient_name: originalVariables.candidate_name || originalVariables.recipient_name || originalVariables.user_name || 'there',
      company_name: originalVariables.company_name || 'Our Team',
      message_content: this.getMessageContent(notificationType, originalVariables),
      ...originalVariables
    };

    // Add notification-specific formatting
    switch (notificationType) {
      case 'application_submitted':
      case 'application_received':
      case 'application_under_review':
      case 'application_approved':
      case 'application_rejected':
        return {
          ...baseVariables,
          show_details: true,
          details_title: 'Application Details',
          details: [
            { label: 'Position', value: originalVariables.job_title || 'N/A' },
            { label: 'Status', value: this.getStatusFromType(notificationType) },
            { label: 'Application Date', value: originalVariables.application_date || new Date().toLocaleDateString() }
          ],
          status: {
            type: this.getStatusTypeFromNotification(notificationType),
            label: this.getStatusFromType(notificationType)
          },
          action_button: originalVariables.application_url ? {
            text: 'View Application',
            url: originalVariables.application_url
          } : undefined
        };

      case 'company_invitation':
        return {
          ...baseVariables,
          show_details: true,
          details_title: 'Invitation Details',
          details: [
            { label: 'Company', value: originalVariables.company_name || 'N/A' },
            { label: 'Role', value: originalVariables.role || 'Member' },
            { label: 'Invited by', value: originalVariables.inviter_name || 'Team' }
          ],
          action_button: originalVariables.invitation_link ? {
            text: 'Accept Invitation',
            url: originalVariables.invitation_link
          } : undefined
        };

      case 'interview_scheduled':
      case 'interview_reminder':
        return {
          ...baseVariables,
          show_details: true,
          details_title: 'Interview Details',
          details: [
            { label: 'Position', value: originalVariables.job_title || 'N/A' },
            { label: 'Date & Time', value: `${originalVariables.interview_date || 'TBD'} at ${originalVariables.interview_time || 'TBD'}` },
            { label: 'Duration', value: originalVariables.interview_duration || '30 minutes' },
            { label: 'Interviewer', value: originalVariables.interviewer_name || 'Team' }
          ],
          action_button: originalVariables.meeting_link ? {
            text: 'Join Meeting',
            url: originalVariables.meeting_link
          } : undefined
        };

      case 'stage_assigned':
        return {
          ...baseVariables,
          show_details: true,
          details_title: 'Task Details',
          details: [
            { label: 'Task', value: originalVariables.stage_name || 'N/A' },
            { label: 'Position', value: originalVariables.job_title || 'N/A' },
            { label: 'Due Date', value: originalVariables.due_date || 'No deadline' },
            { label: 'Estimated Time', value: originalVariables.estimated_time || 'Not specified' }
          ],
          action_button: originalVariables.task_link || originalVariables.application_url ? {
            text: 'Complete Task',
            url: originalVariables.task_link || originalVariables.application_url
          } : undefined,
          additional_info: originalVariables.stage_description ? {
            title: 'Description',
            content: originalVariables.stage_description
          } : undefined
        };

      default:
        return baseVariables;
    }
  }

  private getCategoryFromType(notificationType: string): string {
    if (notificationType.includes('application')) return 'Application';
    if (notificationType.includes('interview')) return 'Interview';
    if (notificationType.includes('company')) return 'Company';
    if (notificationType.includes('stage')) return 'Task';
    return 'Notification';
  }

  private getMessageContent(notificationType: string, variables: Record<string, any>): string {
    switch (notificationType) {
      case 'application_submitted':
        return `Your application for ${variables.job_title || 'the position'} has been successfully submitted and is now under review.`;
      case 'application_received':
        return `A new application has been received for ${variables.job_title || 'your job posting'} from ${variables.candidate_name || 'a candidate'}.`;
      case 'application_under_review':
        return `Your application for ${variables.job_title || 'the position'} is currently being reviewed by our team.`;
      case 'application_approved':
        return `Congratulations! Your application for ${variables.job_title || 'the position'} has been approved. We'll be in touch with next steps.`;
      case 'application_rejected':
        return `Thank you for your interest in ${variables.job_title || 'the position'}. While we've decided to move forward with other candidates, we encourage you to apply for future opportunities.`;
      case 'company_invitation':
        return `${variables.inviter_name || 'Someone'} has invited you to join ${variables.company_name || 'their company'} as a ${variables.role || 'team member'}.`;
      case 'interview_scheduled':
        return `Your interview for ${variables.job_title || 'the position'} has been scheduled. Please review the details below and mark your calendar.`;
      case 'interview_reminder':
        return `This is a reminder about your upcoming interview for ${variables.job_title || 'the position'} tomorrow.`;
      case 'stage_assigned':
        return `You have been assigned a new task for your application to ${variables.job_title || 'the position'}.`;
      default:
        return variables.message || variables.content || 'You have a new notification.';
    }
  }

  private getStatusFromType(notificationType: string): string {
    switch (notificationType) {
      case 'application_submitted': return 'Submitted';
      case 'application_received': return 'New Application';
      case 'application_under_review': return 'Under Review';
      case 'application_approved': return 'Approved';
      case 'application_rejected': return 'Not Selected';
      case 'interview_scheduled': return 'Interview Scheduled';
      case 'stage_assigned': return 'Task Assigned';
      default: return 'Active';
    }
  }

  private getStatusTypeFromNotification(notificationType: string): string {
    switch (notificationType) {
      case 'application_approved': return 'success';
      case 'application_rejected': return 'danger';
      case 'application_under_review': return 'warning';
      default: return 'warning';
    }
  }

  /**
   * Notify about low credits (< 20% of monthly allocation)
   */
  async notifyCreditsLow(
    companyId: string,
    balance: number,
    monthlyCredits: number,
    creditsUsed: number
  ): Promise<UnifiedNotificationResult> {
    try {
      // Get company owner and admins
      const { data: teamMembers } = await supabase
        .from('company_team_members')
        .select('user_id, role, user_profiles(email, full_name)')
        .eq('company_id', companyId)
        .in('role', ['owner', 'admin']);

      if (!teamMembers || teamMembers.length === 0) {
        return {
          success: false,
          error: 'No company admins found'
        };
      }

      // Get company name
      const { data: company } = await supabase
        .from('company_profiles')
        .select('name')
        .eq('id', companyId)
        .single();

      // Calculate next billing date
      const today = new Date();
      const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      const nextBillingDate = nextMonth.toISOString().split('T')[0];

      const results: UnifiedNotificationResult[] = [];

      // Notify all admins
      for (const member of teamMembers) {
        const result = await this.notify({
          notificationType: 'credits_low_warning',
          recipientUserId: member.user_id,
          urgency: 'medium',
          templateVariables: {
            company_name: company?.name || 'Your Company',
            credit_balance: balance,
            monthly_credits: monthlyCredits,
            credits_used: creditsUsed,
            user_name: (member.user_profiles as any)?.full_name || 'Team Member',
            next_billing_date: nextBillingDate,
            dashboard_url: 'https://a.evee.ai/workspace',
            support_email: 'support@evee.ai'
          },
          context: {
            company_id: companyId,
            notification_source: 'credit_monitoring'
          }
        });

        results.push(result);
      }

      // Return aggregated result
      return {
        success: results.every(r => r.success),
        deliveryChannels: {
          email: {
            queued: results.length,
            recipients: teamMembers.map(m => (m.user_profiles as any)?.email)
          }
        }
      };
    } catch (error) {
      console.error('Error notifying low credits:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Notify about depleted credits (balance = 0)
   */
  async notifyCreditsDepleted(
    companyId: string
  ): Promise<UnifiedNotificationResult> {
    try {
      // Get company owner and admins
      const { data: teamMembers } = await supabase
        .from('company_team_members')
        .select('user_id, role, user_profiles(email, full_name)')
        .eq('company_id', companyId)
        .in('role', ['owner', 'admin']);

      if (!teamMembers || teamMembers.length === 0) {
        return {
          success: false,
          error: 'No company admins found'
        };
      }

      // Get company name
      const { data: company } = await supabase
        .from('company_profiles')
        .select('name')
        .eq('id', companyId)
        .single();

      const results: UnifiedNotificationResult[] = [];

      // Notify all admins with high urgency
      for (const member of teamMembers) {
        const result = await this.notify({
          notificationType: 'credits_depleted',
          recipientUserId: member.user_id,
          urgency: 'high',
          templateVariables: {
            company_name: company?.name || 'Your Company',
            credit_balance: 0,
            user_name: (member.user_profiles as any)?.full_name || 'Team Member',
            dashboard_url: 'https://a.evee.ai/workspace',
            support_email: 'support@evee.ai'
          },
          context: {
            company_id: companyId,
            notification_source: 'credit_monitoring'
          }
        });

        results.push(result);
      }

      // Return aggregated result
      return {
        success: results.every(r => r.success),
        deliveryChannels: {
          email: {
            queued: results.length,
            recipients: teamMembers.map(m => (m.user_profiles as any)?.email)
          }
        }
      };
    } catch (error) {
      console.error('Error notifying depleted credits:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
}

// Export singleton instance
export const unifiedNotificationOrchestrator = new UnifiedNotificationOrchestratorService();

// Export convenience function
export const notify = (request: UnifiedNotificationRequest): Promise<UnifiedNotificationResult> => {
  return unifiedNotificationOrchestrator.notify(request);
};